import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import Swal from "sweetalert2";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Redux
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";

// Icons
import { MdDeleteOutline } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { FaAngleRight } from "react-icons/fa";

// MUI
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Button,
  Paper,
} from "@mui/material";

const PRIMARY = "#286a94";

const ShowAllProjects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state.Profile.result);
  const candidateId = userResult?.userid;

  const token = userResult?.token;
  const baseUrl = process.env.REACT_APP_API_PATH;

  useEffect(() => {
    dispatch(CandidateProfile(candidateId));
  }, []);

  /** ---- DELETE PROJECT ---- */
  const handleProjectDelete = (projectId) => {
    Swal.fire({
      title: "Delete Project?",
      text: "Are you sure you want to delete this project?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
      cancelButtonColor: "gray",
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${baseUrl}/ebench/delete_project?projectId=${projectId}`,
            {
              headers: { Authorization: "Bearer " + token },
            }
          );
          toast.success("Project deleted successfully");
          dispatch(CandidateProfile(candidateId));
        } catch {
          toast.error("Failed to delete project");
        }
      }
    });
  };

  /** ---- CAROUSEL ---- */
  const ProjectCarousel = ({ images }) => {
    if (!images || images.length === 0) return null;

    const settings = {
      dots: true,
      infinite: images.length > 1,
      speed: 500,
      slidesToShow: images.length > 2 ? 3 : images.length,
      slidesToScroll: 1,
    };

    return (
      <>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Slider {...settings}>
            {images.map((img, index) => (
              <Box key={index} sx={{ p: 1 }}>
                <a href={img} target="_blank" rel="noreferrer">
                  <img
                    src={img}
                    alt="Project"
                    style={{
                      width: "100%",
                      height: "180px",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                </a>
              </Box>
            ))}
          </Slider>
        </Box>
      </>
    );
  };

  return (
    <>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: "#d32f2f",
          fontSize: "0.875rem",
          mx: "auto",
          fontStyle: "italic",
          mt: 0.5,
        }}
      >
        Note: Project experience duration should be less than or equal to your
        total professional experience.
      </Typography>

      <Box sx={{ maxWidth: "1150px", mx: "auto", mt: 5 }}>
        {/* ------- Page Header ------- */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: PRIMARY,
            }}
          >
            All Projects <FaAngleRight />
          </Typography>

          <Button
            variant="outlined"
            sx={{
              borderColor: PRIMARY,
              color: `${PRIMARY} !important`,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: PRIMARY,
                color: "white !important",
              },
            }}
            component={Link}
            to="/add-candidate-projects"
          >
            Add Project
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ------- PROJECT LIST ------- */}
        {userInfo?.candidateProjects?.length > 0 ? (
          userInfo.candidateProjects.map((item, idx) => (
            <Paper
              key={idx}
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                backgroundColor: "#f7fbff",
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600} color="#193563">
                  #{idx + 1} {item.projectName}{" "}
                  <Box component="span" color="gray" fontWeight={400}>
                    ({item.duration})
                  </Box>
                </Typography>

                <Box>
                  <IconButton
                    onClick={() =>
                      navigate(`/update-candidate-projects/${item.projectId}`)
                    }
                  >
                    <CiEdit size={22} color={PRIMARY} />
                  </IconButton>

                  <IconButton
                    onClick={() => handleProjectDelete(item.projectId)}
                  >
                    <MdDeleteOutline size={22} color="red" />
                  </IconButton>
                </Box>
              </Box>

              <Typography sx={{ mt: 1 }}>
                <strong>Description: </strong>
                <span style={{ whiteSpace: "pre-line", color: "#555" }}>
                  {item.description || "Not Available"}
                </span>
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Role: </strong>
                <span style={{ color: "#555" }}>{item.role || "NA"}</span>
              </Typography>

              <Typography sx={{ mt: 1 }}>
                <strong>Project URL: </strong>
                <span style={{ color: "#555" }}>
                  {item.projectUrl || "N/A"}
                </span>
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{ mt: 1, fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Technologies:
                </Typography>
                {item.technologies?.map((tech, i) => (
                  <Chip
                    label={tech}
                    key={i}
                    size="small"
                    sx={{
                      backgroundColor: PRIMARY,
                      color: "white",
                      mt: 1,
                    }}
                  />
                ))}
              </Box>

              {item.projectImages && item.projectImages.length > 0 && (
                <ProjectCarousel images={item.projectImages} />
              )}
            </Paper>
          ))
        ) : (
          <Typography textAlign="center" sx={{ mt: 5, color: "gray" }}>
            No projects added yet.
          </Typography>
        )}
      </Box>
    </>
  );
};

export default ShowAllProjects;
